<script lang="ts">
  import { isIncluded, mirroredComment, MIRRORED_DISCARD, originText, stateText } from '../logic/inbox';
  import { commentKeys, keyboard } from '../keys/keyboard.svelte';
  import type { ReviewThread } from '../api/queries';
  import type { ReviewDraft } from '../api/server';
  import { subject } from '../state/pr.svelte';
  import type { Conversation, ThreadPage } from '../state/rangeHost';
  import { onDestroy, tick } from 'svelte';
  import RelayThread from './RelayThread.svelte';
  import { pullKey } from '../api/relaySubject';
  import { THREAD_NEEDS, threadTopic } from '../api/relayTask';
  import { relayAgents } from '../state/relayAgents.svelte';
  import { relay } from '../api/relay';
  import { MOCK } from '../mock/flag';
  import Html from './Html.svelte';
  import PendingTag from './PendingTag.svelte';
  import SaveButton from './SaveButton.svelte';
  import CommentBody from './CommentBody.svelte';
  import SnapshotChip from './SnapshotChip.svelte';
  import { loadDraft, mergeText, saveDraft } from '../state/drafts';
  import { IDLE, tellers } from '../state/pending.svelte';
  import { focusAt, handsFocusOn } from '../logic/focus';
  import { relTime } from '../logic/time';
  import { commentCommit } from '../logic/timeline';
  import { chipTarget, type ChipTarget } from '../logic/snapshotLink';
  import { blobUrl, lineLabel, parseOffDiff, replaceOffDiffText } from '../logic/offdiff';
  import { session } from '../state/session.svelte';
  import { reveal } from '../state/reveal.svelte';
  import { planThread, threadReview, THREAD_PATH } from '../review/threadMarks';

  interface Props {
    pr: Conversation;
    /** The page the thread is on: access, and the marks set on threads. */
    page: ThreadPage;
    thread: ReviewThread;
    blockKey?: string;
    showPath?: boolean;
  }
  let { pr, page, thread, blockKey = '', showPath = false }: Props = $props();

  const isDraft = $derived(thread.comments.nodes.every((c) => c.pullRequestReview?.state === 'PENDING'));
  // Resolved threads start collapsed; afterwards the user's choice wins.
  // svelte-ignore state_referenced_locally
  let expanded = $state(!thread.isResolved);
  // Asked to show itself (?thread=): open, whatever it started as or the
  // reader made it since. Each request opens it once.
  $effect(() => {
    if (reveal.request?.id === thread.id) expanded = true;
  });
  let replying = $state(false);
  /** The Local agent panel is open under the thread's actions. */
  let relayOpen = $state(false);
  /** The reader opened or closed it: what this browser sent no longer opens it. */
  let relayToggled = false;
  // Sent to an agent from this browser: open, so its answer shows without asking.
  /** The agents this browser sent the thread to (from any tab, before now). */
  let sentTo = $state<string[]>([]);
  // svelte-ignore state_referenced_locally (a thread's number and ID are fixed for its life)
  if (session.agents) void relay.sentTo(threadTopic(pullKey(pr.owner, pr.repo, pr.number), thread.id)).then((ids) => { sentTo = ids; if (ids.length && !relayToggled) relayOpen = true; }, () => {});
  // Sent from this tab since (the composer's Save & send): the same.
  $effect(() => {
    if (relayAgents.sentTopics.includes(threadTopic(pullKey(pr.owner, pr.repo, pr.number), thread.id)) && !relayToggled) relayOpen = true;
  });
  /** The agents this thread can be sent to, and the one Send to names (the first, unless another is picked). */
  const agents = $derived(!session.agents ? [] : relayAgents.taskAgents(pullKey(pr.owner, pr.repo, pr.number), THREAD_NEEDS));
  let picked = $state('');
  const agent = $derived(agents.find((c) => c.id === picked) ?? agents[0]);
  /** Whether the thread already went to that agent: then the control opens its results instead of sending again. */
  const sentToAgent = $derived(!!agent && sentTo.includes(agent.id));
  let relayPanel: RelayThread | undefined = $state();
  /** Send to <agent>: opens the panel and sends the thread with the default request (RelayThread.sendNow). */
  async function sendToAgent() {
    if (!agent) return;
    relayToggled = true;
    relayOpen = true;
    await tick();
    const to = agent.id;
    await relayPanel?.sendNow(to);
    if (!sentTo.includes(to)) sentTo = [...sentTo, to];
  }
  // Reviewed: the person checked the thread (one someone else resolved,
  // say) as it is now. Private to them, kept with their review marks; a
  // reply or a resolution since shows it as changed since reviewed.
  const review = $derived(threadReview(thread, page.marks.flags));
  // A draft thread set aside (review/asides.ts: the viewer's own, out of
  // the review, a conversation with their agent, say) starts collapsed,
  // and says so.
  const firstDraft = $derived(isDraft ? pr.draftOfComment(thread.comments.nodes[0]?.id ?? '') : null);
  const asideDraft = $derived(!!firstDraft && pr.isAside(firstDraft.id));
  // Set aside (here or anywhere), it folds; brought back, it stays as the person left it.
  let wasAside = false;
  $effect(() => {
    if (asideDraft && !wasAside) expanded = false;
    wasAside = asideDraft;
  });
  /** Thread marks belong to published threads; a draft is the person's own. */
  const reviewable = $derived(!isDraft && !thread.id.startsWith('draft:'));
  const reviewSaving = $derived(page.marks.unsentFlags.some((a) => a.path === THREAD_PATH + thread.id));
  /** Why marks can't be kept here (no server data), or null. */
  const reviewOff = $derived(page.serverDataText);
  function markReviewed(on: boolean) {
    if (reviewOff !== null) return;
    page.marks.apply(planThread(thread, page.marks.flags, on));
  }
  let reply = $state('');
  /** A write in flight on the whole thread (resolving it, or saving it as a new draft): it is dimmed and its buttons are off. */
  const busy = $derived(pr.writes.on(subject.thread(thread.id)));
  const resolvesLater = $derived(pr.resolveOnPublish.includes(thread.id));
  /** The viewer already drafted Done/Ack here, so the buttons would only add duplicates. */
  const quickReplied = $derived(pr.quickReplied(thread));
  /** Done/Ack resolve the thread on publish only when the viewer may resolve it. */
  const quickTitle = (word: 'Done' | 'Ack') =>
    thread.viewerCanResolve
      ? `Draft the reply “${word}”; the thread resolves when you publish`
      : `Draft the reply “${word}”`;
  const draftKey = $derived(`reply:${thread.id}`);
  /** How saving the reply stands (see `Writes.box`): the reply box stays open through it, and tells it. */
  const replySave = $derived(pr.writes.box(draftKey));
  // svelte-ignore state_referenced_locally (the store is the thread's for its life)
  const mine = tellers(pr.writes);
  onDestroy(mine.release);
  /** Writes fail on a read-only repository: the write buttons are off, with the reason as their tooltip. */
  const readOnlyTitle = $derived(page.readOnly ? (page.readOnlyText ?? undefined) : undefined);
  /** Drafts (replies, Done and Ack) are held by the server: off with GitHub writes or with server data. */
  const draftsOffTitle = $derived(pr.draftsOffText ?? undefined);
  let root: HTMLDivElement | undefined = $state();
  const first = $derived(thread.comments.nodes[0]);
  /** A file-level thread on a line outside the diff: its first comment names the line (changelist:anchor). */
  const anchored = $derived.by(() => {
    if (thread.subjectType !== 'FILE') return null;
    const p = parseOffDiff(first?.body);
    return p && p.marker.path === thread.path ? p : null;
  });
  const permalink = $derived(
    anchored ? blobUrl(session.config?.github_web_url ?? 'https://github.com', pr.owner, pr.repo, anchored.marker) : null,
  );
  const range = $derived(
    anchored
      ? lineLabel(anchored.marker)
      : thread.subjectType === 'FILE'
      ? 'file'
      : linesLabel(),
  );
  /** The thread's lines on the head, else (outdated) its original ones; a range starting on the other side by its end alone. */
  function linesLabel(): string {
    const head = thread.line !== null;
    const end = head ? thread.line : thread.originalLine;
    const start = (thread.startDiffSide ?? thread.diffSide) === thread.diffSide ? (head ? thread.startLine : thread.originalStartLine) : null;
    return start && start !== end ? `lines ${start}–${end}` : `line ${end}`;
  }

  /**
   * Where a chip dated to snapshot `sha` opens: the anchored lines there;
   * else the thread's lines on `sha`, or, where it has none there, its
   * original lines on the snapshot it was written on (`chipTarget`).
   */
  function chipPlace(sha: string, head: string): ChipTarget {
    if (anchored) {
      const m = anchored.marker;
      return { sha, spot: { side: m.side, start: Math.min(m.start ?? m.line, m.line), end: m.line } };
    }
    return chipTarget(thread, sha, head);
  }

  /** Expand or collapse, keeping keyboard focus in the thread (its summary, else its first comment). */
  async function setExpanded(v: boolean) {
    expanded = v;
    await tick();
    const target = root?.querySelector<HTMLElement>('.thread-summary') ?? root?.querySelector<HTMLElement>('.comment');
    target?.focus({ preventScroll: true });
  }

  /**
   * Close the reply box and give focus back to Reply. `saved`: it closes
   * because the server has the reply, at a moment the person did not
   * choose, so the focus goes to Reply only if it is still in the box (or
   * nowhere), never from wherever the person is typing by then.
   */
  async function closeReply(saved = false) {
    const focus = focusAt(root?.querySelector('.composer'));
    replying = false;
    mine.release();
    if (saved && !handsFocusOn(focus)) return;
    await tick();
    root?.querySelector<HTMLElement>('.thread-reply')?.focus({ preventScroll: true });
  }

  /** Counts openings of the reply box: a save answered after the box was closed is not the box opened since. */
  let opened = 0;

  async function openReply() {
    replying = true;
    opened++;
    // A reply being sent, or offered for Retry, is not offered again (see Writes.holds).
    const saved = await loadDraft(draftKey, { outbox: !pr.writes.holds(draftKey) });
    if (!replying) return;
    // A reply that was not saved is told here, with its text, and no longer by the toast.
    const failed = pr.writes.box(draftKey);
    if (failed.at === 'failed') mine.tell(draftKey);
    // Text typed while the saved draft was loading stays, with it.
    reply = mergeText(reply, failed.at === 'failed' ? mergeText(saved, failed.text ?? '') : saved);
  }

  /**
   * Save the reply. From the press the box is saving: its button says so
   * and takes no second press. Once the server has the reply the box
   * closes, and the reply is in the thread. If saving fails the box stays
   * as it was, says why beside the button, and sends again on the next
   * press. Closed before it is answered, the reply shows in the thread as
   * being saved, and a failure is told by the toast.
   */
  async function send() {
    if (!reply.trim() || pr.draftsOff || replySave.at === 'saving') return;
    const sent = reply;
    const box = opened;
    mine.tell(draftKey);
    if (!(await pr.reply(thread.id, sent, { key: draftKey, text: sent }))) return;
    // Still the box that sent it (not closed, or opened again, meanwhile).
    if (!replying || opened !== box) return;
    reply = '';
    void closeReply(true);
  }

  // While the reply box is open: a failed reply given up comes back, beside what was typed since
  // (a box opened later finds it in the saved draft).
  $effect(() => {
    if (replying) return pr.writes.listen(draftKey, (t) => (reply = mergeText(reply, t)));
  });

  /** Delete a draft shown in the thread; one that is a comment in the viewer's pending review on GitHub goes from GitHub too, so that is asked first. */
  function deleteDraft(commentId: string) {
    const own = pr.draftOfComment(commentId);
    if (own && mirroredComment(own) !== null && !confirm(MIRRORED_DISCARD)) return;
    void pr.deleteDraftComment(commentId);
  }

  // ---------------------------------------------------------------- editing a draft in place
  /** The draft comment being edited (one at a time), and its text. */
  let editing = $state<{ comment: string; draft: ReviewDraft; anchoredText: boolean } | null>(null);
  let editText = $state('');
  /** Unsent edit text, kept like every other comment being typed (the key the Review tab's card and a held note edit it under too). */
  const editKey = $derived(editing ? `held:${editing.draft.id}` : '');
  /** How saving the edit stands (see `Writes.box`): the editor stays open through it, and tells it. */
  const editSave = $derived(editKey ? pr.writes.box(editKey) : IDLE);
  /** Counts openings of the editor: a save answered after it was closed is not the editor opened since. */
  let edits = 0;

  /** Edit the viewer's draft `draft`, shown as comment `comment` (the thread's first, on an off-diff line, without its marker). */
  async function openEdit(comment: string, draft: ReviewDraft, anchoredText: boolean) {
    editing = { comment, draft, anchoredText };
    editText = anchoredText && anchored ? anchored.text : draft.body;
    edits++;
    const key = editKey;
    const original = editText;
    // An edit being sent, or offered for Retry, is not offered again (see Writes.holds).
    const saved = await loadDraft(key, { outbox: !pr.writes.holds(key) });
    if (editing?.comment !== comment || key !== editKey) return;
    // An edit that was not saved is told here, with its text, and no longer by the toast.
    const failed = pr.writes.box(key);
    if (failed.at === 'failed') mine.tell(key);
    const unsent = failed.at === 'failed' ? mergeText(saved, failed.text ?? '') : saved;
    // An edit left unsent earlier comes back, unless typing has started meanwhile.
    if (unsent && editText === original) editText = unsent;
  }

  /** Close the editor (its text stays saved as a draft) and give focus back to the comment's Edit; `saved` as for `closeReply`. */
  async function closeEdit(saved = false) {
    const comment = editing?.comment;
    const focus = focusAt(root?.querySelector('.comment-edit'));
    editing = null;
    mine.release();
    if (saved && !handsFocusOn(focus)) return;
    await tick();
    root?.querySelector<HTMLElement>(`[data-comment="${comment}"] .draft-edit`)?.focus({ preventScroll: true });
  }

  /**
   * Save the edit. From the press the editor is saving: its button says so
   * and takes no second press. Once the server has the edit the editor
   * closes on the new text. If saving fails the editor stays as it was,
   * says why beside the button, and sends again on the next press.
   */
  async function saveEdit() {
    if (!editing || !editText.trim() || pr.draftsOff || editSave.at === 'saving') return;
    const { draft, anchoredText } = editing;
    const sent = editText;
    const editor = edits;
    const key = editKey;
    mine.tell(key);
    const body = anchoredText ? replaceOffDiffText(draft.body, sent) : sent;
    if (!(await pr.editDraft(draft.id, body, { key, text: sent }))) return;
    if (editing && edits === editor) void closeEdit(true);
  }

  // While the editor is open: a failed edit given up comes back, beside what was typed since.
  $effect(() => {
    if (editKey) return pr.writes.listen(editKey, (t) => (editText = mergeText(editText, t)));
  });

  const editKeys = commentKeys((c) => {
    if (c === 'submit') void saveEdit();
    else void closeEdit();
  });

  /** Resolve (it collapses at once, as resolved) or unresolve; a failure opens it again. */
  async function resolve(resolved: boolean) {
    if (resolved) void setExpanded(false);
    if (!(await pr.setResolved(thread.id, resolved)) && resolved) expanded = true;
  }

  // Esc closes the reply; the focus goes back to Reply, not to the diff reader.
  const keys = commentKeys((c) => {
    if (c === 'submit') void send();
    else void closeReply();
  });
</script>

{#snippet reviewedTag()}
  <span class="thread-review-tag" class:changed={review === 'changed'} title={review === 'changed' ? 'You marked this thread reviewed; something came since' : 'You marked this thread reviewed'}>{review === 'changed' ? '✓ changed since' : '✓ reviewed'}</span>
{/snippet}

<div
  bind:this={root}
  class="thread"
  class:is-pending={!!busy}
  aria-busy={busy ? 'true' : undefined}
  class:unresolved={!thread.isResolved && !isDraft}
  class:draft={isDraft}
  class:resolved={thread.isResolved}
  data-thread={thread.id}
  data-block={blockKey}
>
  {#if !expanded}
    <button class="thread-summary" onclick={() => setExpanded(true)} aria-expanded="false">
      <span>▸</span>
      <b>{first?.author?.login ?? 'ghost'}</b>
      <span class="snippet">{anchored ? anchored.text : (first?.body ?? '')}</span>
      <span>{thread.comments.totalCount} comment{thread.comments.totalCount === 1 ? '' : 's'} · {asideDraft ? 'set aside' : thread.isResolved ? 'resolved' : 'open'}{thread.isOutdated ? ' · outdated' : ''}</span>
      {#if reviewable && review !== 'none'}{@render reviewedTag()}{/if}
      {#if busy}<PendingTag label={busy.label} />{/if}
    </button>
  {:else}
    {#if thread.isResolved || showPath || thread.isOutdated || asideDraft}
      <button class="thread-summary" onclick={() => setExpanded(false)} aria-expanded="true">
        <span>▾</span>
        {#if showPath}<span class="mono">{thread.path} · {range}</span>{/if}
        <span class="snippet"></span>
        <span>{asideDraft ? 'Set aside, out of your review' : thread.isResolved ? 'Resolved' + (thread.resolvedBy ? ' by ' + thread.resolvedBy.login : '') : 'Open'}</span>
        {#if reviewable && review !== 'none'}{@render reviewedTag()}{/if}
        {#if busy}<PendingTag label={busy.label} />{/if}
      </button>
    {/if}
    {#each thread.comments.nodes as c, i (c.id)}
      {@const pending = c.pullRequestReview?.state === 'PENDING'}
      {@const commit = commentCommit(thread.comments.nodes, i)}
      {@const saving = pr.writes.on(subject.comment(c.id))}
      {@const own = pending ? pr.draftOfComment(c.id) : null}
      <!-- An edit of the draft being saved, with its editor closed: Edit is off until the answer. -->
      {@const ownBusy = own && editing?.comment !== c.id ? pr.writes.on(subject.draft(own.id)) : null}
      <!-- A reply being saved is on screen in the reply box while that is open. -->
      {#if !pr.writes.shownInBox(subject.comment(c.id))}
      <div class="comment" class:is-pending={!!saving} aria-busy={saving ? 'true' : undefined} tabindex="-1" data-comment={c.id}>
        <div class="comment-head">
          {#if c.author?.avatarUrl}<img class="avatar" src={c.author.avatarUrl} alt="" />{/if}
          <b>{c.author?.login ?? 'ghost'}</b>
          <span title={c.createdAt}>{relTime(c.createdAt)}</span>
          {#if pending}<span class="badge draft">draft</span>{/if}
          {#if own}
            <span
              class="draft-state"
              class:on={isIncluded(own)}
              title={isIncluded(own) ? 'Ticked: your next publish sends it' : 'Not sent until you tick it, on the Review tab'}
              >{stateText(own)}</span
            >
            {#if originText(own)}<span class="draft-origin-chip" title={originText(own)?.why ?? undefined}>{originText(own)?.note ?? originText(own)?.label}</span>{/if}
          {/if}
          <SnapshotChip
            {pr}
            {commit}
            at={c.createdAt}
            path={thread.path}
            place={chipPlace}
            thread={thread.id}
            comment={i === 0 ? null : c.id}
            outdated={i === 0 ? { github: thread.isOutdated } : null}
          />
          {#if i === 0 && anchored && permalink}
            <a class="anchor-chip" href={permalink} target="_blank" rel="noreferrer" title="This line on GitHub at {anchored.marker.commit.slice(0, 7)}"
              >{showPath ? thread.path + ' · ' : ''}{range}{anchored.marker.side === 'LEFT' ? ' (old)' : ''} ↗</a
            >
          {/if}
          {#if saving}
            <span style="margin-left:auto"><PendingTag label={saving.label} /></span>
          {:else if busy && i === 0 && isDraft}
            <span style="margin-left:auto"><PendingTag label={busy.label} /></span>
          {:else if pending && !busy}
            <span class="comment-actions">
              {#if own && editing?.comment !== c.id}
                <button class="micro draft-edit" disabled={pr.draftsOff || !!ownBusy} title={draftsOffTitle ?? ownBusy?.label ?? 'Edit this draft'} onclick={() => openEdit(c.id, own, i === 0 && !!anchored)}>Edit</button>
              {/if}
              <button class="micro" disabled={pr.draftsOff} title={draftsOffTitle} onclick={() => deleteDraft(c.id)}>Delete draft</button>
              {#if own}
                {@const aside = pr.isAside(own.id)}
                <button class="micro draft-aside" disabled={page.serverDataText !== null} title={page.serverDataText ?? (aside ? 'Back to the drafts for your review (not included until you tick it)' : 'Keep it for yourself, out of this review (a conversation with your agent, say): listed under Set aside on the Review tab, never sent')} onclick={() => pr.setAside(own.id, !aside)}>{aside ? 'Bring back' : 'Set aside'}</button>
              {/if}
            </span>
          {/if}
        </div>
        {#if editing?.comment === c.id}
          <div class="composer comment-edit" style="border-color:var(--line)">
            <!-- svelte-ignore a11y_autofocus -->
            <textarea
              autofocus
              aria-label="Edit draft"
              bind:value={editText}
              readonly={editSave.at === 'saving'}
              oninput={() => editKey && saveDraft(editKey, editText)}
              {@attach editKeys}
            ></textarea>
            <div class="row">
              <span class="spacer"></span>
              <button class="micro" onclick={() => closeEdit()}>{editSave.at === 'saving' ? 'Close' : 'Cancel'}</button>
              <SaveButton save={editSave} label="Save" disabled={!editText.trim() || pr.draftsOff} title={draftsOffTitle} onsave={saveEdit} />
            </div>
          </div>
        {:else if i === 0 && anchored}<Html md={anchored.text} />{:else}<CommentBody {pr} {page} {thread} comment={c} />{/if}
      </div>
      {/if}
    {/each}
    {#if replying}
      <div class="comment">
        <div class="composer" style="border-color:var(--line)">
          <!-- svelte-ignore a11y_autofocus -->
          <textarea
            autofocus
            aria-label="Reply"
            placeholder="Reply…{keyboard.keysIn('field', { kind: 'submit' }) ? ` (${keyboard.keysIn('field', { kind: 'submit' })} to save as draft)` : ''}"
            bind:value={reply}
            readonly={replySave.at === 'saving'}
            oninput={() => saveDraft(draftKey, reply)}
            {@attach keys}
          ></textarea>
          <div class="row">
            <span class="where">Kept in your drafts until you publish.</span><span class="spacer"></span>
            <button class="micro" onclick={() => closeReply()}>{replySave.at === 'saving' ? 'Close' : 'Cancel'}</button>
            <SaveButton save={replySave} label="Add reply" disabled={!reply.trim() || pr.draftsOff} title={draftsOffTitle} onsave={send} />
          </div>
        </div>
      </div>
    {:else}
      <div class="thread-actions">
        {#if thread.viewerCanReply}<button class="micro thread-reply" onclick={openReply} disabled={pr.draftsOff} title={draftsOffTitle}>Reply</button>{/if}
        {#if resolvesLater}
          <span class="resolves-later" title="Your draft reply resolves this thread when you publish your review">Resolves when you publish</span>
        {:else if !thread.isResolved && !isDraft && thread.viewerCanReply && !quickReplied}
          <button class="micro" disabled={!!busy || pr.draftsOff} onclick={() => pr.quickReply(thread.id, 'Done')} title={draftsOffTitle ?? quickTitle('Done')}>Done</button>
          <button class="micro" disabled={!!busy || pr.draftsOff} onclick={() => pr.quickReply(thread.id, 'Ack')} title={draftsOffTitle ?? quickTitle('Ack')}>Ack</button>
        {/if}
        {#if agent}
          <!-- Sends at once with the default request; the panel after it shows how it goes, and edits the request. -->
          <span class="agent-send" role="group" aria-label="Local agent">
            {#if sentToAgent}
              <!-- Sent already: nothing here sends. One toggle shows or hides the answer; the panel's Send again… sends anew. -->
              <button class="micro" aria-expanded={relayOpen} title={relayOpen ? `Hide what ${agent.name} answered` : `Show what ${agent.name} answered`} onclick={() => { relayToggled = true; relayOpen = !relayOpen; }}>{agent.name}’s answer {relayOpen ? '▴' : '▾'}</button>
            {:else}
              <button class="micro" title="Send this thread to {agent.name} on your computer, asking it to address the feedback" onclick={sendToAgent}>Send to {agent.name}</button>
            {/if}
            {#if agents.length > 1}
              <select class="micro" aria-label="Agent to send to" value={agent.id} onchange={(e) => (picked = e.currentTarget.value)}>
                {#each agents as c (c.id)}<option value={c.id}>{c.name}</option>{/each}
              </select>
            {/if}
            {#if !sentToAgent}
              <button class="micro agent-more" aria-expanded={relayOpen} aria-label="Local agent panel" title="Status, results and the request" onclick={() => { relayToggled = true; relayOpen = !relayOpen; }}>{relayOpen ? '▴' : '▾'}</button>
            {/if}
          </span>
        {:else if session.agents || MOCK}
          <button class="micro" disabled={MOCK} title={MOCK ? 'Local agents are unavailable in the demo' : 'Send this thread to an agent on your computer'} aria-expanded={relayOpen} onclick={() => { relayToggled = true; relayOpen = !relayOpen; }}>Local agent</button>
        {/if}
        <span class="spacer"></span>
        {#if reviewable}
          <label class="review-toggle thread-reviewed" class:changed={review === 'changed'} class:unavailable={reviewOff !== null} class:is-pending={reviewSaving} aria-busy={reviewSaving ? 'true' : undefined} title={reviewOff ?? (review === 'reviewed' ? 'You checked this thread as it is now. Untick to clear your mark.' : review === 'changed' ? 'Something came since you marked it: tick to mark it reviewed as it is now.' : 'Mark that you checked this thread (private to you)')}>
            <input type="checkbox" checked={review === 'reviewed'} aria-disabled={reviewOff !== null ? 'true' : undefined} onclick={(e) => reviewOff !== null && e.preventDefault()} onchange={(e) => markReviewed(e.currentTarget.checked)} />
            <span>{review === 'changed' ? 'Reviewed · changed since' : 'Reviewed'}</span>
          </label>
        {/if}
        {#if thread.isResolved}
          {#if thread.viewerCanUnresolve}<button class="micro" disabled={!!busy || page.readOnly} title={readOnlyTitle} onclick={() => resolve(false)}>Unresolve</button>{/if}
        {:else if thread.viewerCanResolve && !isDraft}
          <button class="micro" disabled={!!busy || page.readOnly} title={readOnlyTitle} onclick={() => resolve(true)}>Resolve</button>
        {/if}
      </div>
    {/if}
    {#if relayOpen}<RelayThread bind:this={relayPanel} {pr} {thread} />{/if}
  {/if}
</div>
